import { Typography, type TypographyProps } from "@mui/material";
import { type Variant } from "@mui/material/styles/createTypography";
import { type ReactElement } from "react";

import { mergeSxProps } from "../../Mui";
import { type RestrictedTypographyTypes } from "../types";

/**
 * @deprecated Use the TextVariant from cbh-mobile-app's redesign system (ui-components has been removed from cbh-core) instead.
 * We restrict text to a fixed set of variants.
 */
export type TextVariant = Exclude<Variant, "button" | "overline">;

/**
 * @deprecated Use the TextProps from cbh-mobile-app's redesign system (ui-components has been removed from cbh-core) instead.
 */
export interface TextProps extends Omit<
  TypographyProps<"p">,
  "variant" | "component" | RestrictedTypographyTypes
> {
  variant?: TextVariant;
  light?: boolean;
  bold?: boolean;
  semibold?: boolean;
  italic?: boolean;
}

/**
 * @deprecated Use the Text component from cbh-mobile-app's redesign system (ui-components has been removed from cbh-core) instead.
 * WCAG 2.1 compliant Text component.
 * ---
 *
 * In order to meet WCAG 2.1 accessibility guidelines, this component
 * is always a `p` element.
 *
 * You can choose a number of variants, to achieve your designer's style.
 *
 * See: https://www.notion.so/BP-Title-and-Text-Document-Hierarchy-5931375db62f464a8169de791f82c9da
 */
export function Text(props: TextProps): ReactElement {
  const { bold, semibold, italic, light, sx, ...otherProps } = props;
  return (
    <Typography
      {...otherProps}
      component="p"
      sx={mergeSxProps(
        [
          {
            fontWeight: (theme) => {
              if (bold) {
                return theme.typography.fontWeightBold;
              }

              if (semibold) {
                return theme.typography.fontWeightMedium;
              }

              if (light) {
                return theme.typography.fontWeightLight;
              }

              return theme.typography.fontWeightRegular;
            },
          },
          { fontStyle: italic ? "italic" : "normal" },
        ],
        sx
      )}
    />
  );
}
